import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, Users, CalendarClock, X, Plus, Trash2, CheckCircle2, ChevronDown, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function ServicePractitionersPanel() {
  const qc = useQueryClient();
  const [availFor, setAvailFor] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["mgr-service-practitioners"],
    queryFn: () => api.get("/manager/me/service-practitioners").then((r) => r.data),
  });

  if (isLoading) return <Loader2 className="animate-spin text-[#144F75]" />;
  const roster = data?.roster || [];
  const services = data?.services || [];

  return (
    <div data-testid="mgr-services-panel">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-1 flex items-center gap-2"><Users className="text-[#144F75]" /> Practitioners & availability</h2>
      <p className="text-sm text-[#4A5D6B] mb-5">Assign the enhancers on your roster to each service, mark services as by-appointment, and set each practitioner's appointment availability at your center.</p>

      {roster.length === 0 && (
        <p className="text-sm text-[#8FA89B] eyw-card p-6 text-center mb-6">Add enhancers to your roster (Enhancers tab) before assigning them to services.</p>
      )}

      {/* Roster availability quick access — shows each practitioner's weekly hours inline */}
      {roster.length > 0 && (
        <div className="eyw-card p-4 mb-6" data-testid="roster-availability">
          <p className="text-sm font-semibold text-[#1A242B] mb-3 flex items-center gap-2"><CalendarClock size={16} className="text-[#144F75]" /> Appointment availability</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {roster.map((e) => {
              const slots = [...(e.availability || [])].sort((a, b) => (a.day - b.day) || String(a.start).localeCompare(String(b.start)));
              return (
                <div key={e.enhancer_id} className="rounded-xl border border-[#E5E0D8] p-3 flex items-start justify-between gap-3" data-testid={`avail-card-${e.enhancer_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1A242B] truncate">{e.name || e.email || "Practitioner"}</p>
                    {slots.length === 0 ? (
                      <p className="text-xs text-[#B5794E] mt-1" data-testid={`avail-none-${e.enhancer_id}`}>No availability set — bookable any time a room is free</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`avail-slots-${e.enhancer_id}`}>
                        {slots.map((s, i) => (
                          <span key={i} className="text-[11px] bg-[#EDF3F7] text-[#144F75] rounded-full px-2 py-0.5">
                            {DAYS[s.day]} {s.start}–{s.end}{s.location_id === "virtual" ? " · online" : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setAvailFor(e)} data-testid={`avail-open-${e.enhancer_id}`}
                    className="shrink-0 rounded-full border border-[#E5E0D8] px-3 py-1.5 text-xs text-[#4A5D6B] hover:border-[#144F75] hover:text-[#144F75] flex items-center gap-1.5">
                    <CalendarClock size={12} /> Edit hours
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {services.length === 0 ? (
        <p className="text-sm text-[#8FA89B] eyw-card p-6 text-center">No services are hosted at your center yet.</p>
      ) : (
        <div className="space-y-3">
          {services.map((s) => (
            <ServiceRow key={s.id} service={s} roster={roster}
              onSaved={() => qc.invalidateQueries({ queryKey: ["mgr-service-practitioners"] })} />
          ))}
        </div>
      )}

      {availFor && (
        <AvailabilityModal enhancer={availFor} onClose={() => setAvailFor(null)} />
      )}
    </div>
  );
}

function ServiceRow({ service, roster, onSaved }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(service.practitioner_ids || []);
  const [byAppt, setByAppt] = useState(!!service.by_appointment);
  const [ov, setOv] = useState(() => {
    const m = {};
    Object.entries(service.overrides || {}).forEach(([k, v]) => {
      m[k] = { price: v?.price ?? "", duration_min: v?.duration_min ?? "" };
    });
    return m;
  });
  const [saving, setSaving] = useState(false);

  const initialOv = JSON.stringify(service.overrides || {});
  const curOv = JSON.stringify(Object.fromEntries(selected.map((id) => [id, {
    price: ov[id]?.price === "" || ov[id]?.price == null ? null : Number(ov[id].price),
    duration_min: ov[id]?.duration_min === "" || ov[id]?.duration_min == null ? null : Number(ov[id].duration_min),
  }])));
  const dirty = JSON.stringify([...selected].sort()) !== JSON.stringify([...(service.practitioner_ids || [])].sort())
    || byAppt !== !!service.by_appointment
    || (service.is_center_service && curOv !== initialOv);

  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const setOvField = (id, k, v) => setOv({ ...ov, [id]: { ...(ov[id] || {}), [k]: v } });

  const rosterById = Object.fromEntries(roster.map((r) => [r.enhancer_id, r]));
  const missingAvail = byAppt ? selected.filter((id) => rosterById[id] && !rosterById[id].has_availability) : [];

  const save = async () => {
    setSaving(true);
    try {
      const overrides = service.is_center_service ? selected.map((id) => ({
        enhancer_id: id,
        price: ov[id]?.price === "" || ov[id]?.price == null ? null : Number(ov[id].price),
        duration_min: ov[id]?.duration_min === "" || ov[id]?.duration_min == null ? null : Number(ov[id].duration_min),
      })) : [];
      await api.put(`/manager/services/${service.id}/practitioners`, { enhancer_ids: selected, by_appointment: byAppt, overrides });
      toast.success("Service practitioners updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save."); }
    finally { setSaving(false); }
  };

  const names = selected.map((id) => (rosterById[id]?.name || "")).filter(Boolean);
  const rowWarn = service.by_appointment && (service.practitioner_ids || []).some((id) => rosterById[id] && !rosterById[id].has_availability);

  return (
    <div className="eyw-card p-4" data-testid={`svc-row-${service.id}`}>
      <div className="flex items-center justify-between gap-3 cursor-pointer" onClick={() => setOpen(!open)}>
        <div>
          <p className="text-sm font-semibold text-[#1A242B]">{service.title}
            {service.by_appointment && <span className="ml-2 text-[11px] bg-[#144F75]/10 text-[#144F75] rounded-full px-2 py-0.5">By appointment</span>}
            {rowWarn && <span data-testid={`svc-warn-${service.id}`} className="ml-2 text-[11px] bg-[#B4463A]/10 text-[#B4463A] rounded-full px-2 py-0.5 inline-flex items-center gap-1"><AlertTriangle size={11} /> Availability missing</span>}
          </p>
          <p className="text-xs text-[#8FA89B]">{names.length ? `${names.length} practitioner(s): ${names.join(", ")}` : "No practitioners assigned"}</p>
        </div>
        <ChevronDown size={18} className={`text-[#8FA89B] transition-transform ${open ? "rotate-180" : ""}`} />
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-[#E5E0D8]">
          <label className="flex items-center gap-2 text-sm text-[#2A3942] cursor-pointer mb-3">
            <input type="checkbox" checked={byAppt} onChange={(e) => setByAppt(e.target.checked)} className="accent-[#144F75]" data-testid={`svc-byappt-${service.id}`} />
            By appointment only (members pick a practitioner + a slot within their availability)
          </label>

          {missingAvail.length > 0 && (
            <div data-testid={`svc-avail-warning-${service.id}`} className="mb-3 rounded-xl bg-[#B4463A]/[0.07] border border-[#B4463A]/20 px-3 py-2.5 text-xs text-[#B4463A] flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>These practitioners have <strong>no availability set</strong>, so they'd be bookable any time a room is free: <strong>{missingAvail.map((id) => rosterById[id]?.name).join(", ")}</strong>. Set their availability to control appointment slots.</span>
            </div>
          )}

          <p className="text-xs font-semibold text-[#4A5D6B] uppercase tracking-wide mb-2">Available practitioners</p>
          {roster.length === 0 ? (
            <p className="text-xs text-[#8FA89B]">No roster enhancers to assign.</p>
          ) : (
            <div className="space-y-1.5 mb-3">
              {roster.map((e) => {
                const on = selected.includes(e.enhancer_id);
                return (
                  <div key={e.enhancer_id} className="rounded-lg hover:bg-[#F9F7F3] px-2 py-1.5" data-testid={`svc-${service.id}-enh-${e.enhancer_id}`}>
                    <label className="flex items-center gap-2 text-sm text-[#2A3942] cursor-pointer">
                      <input type="checkbox" checked={on} onChange={() => toggle(e.enhancer_id)} className="accent-[#144F75]" />
                      {e.name || e.email}
                      {!e.has_availability && <span className="text-[10px] text-[#B5794E]">(no availability set)</span>}
                    </label>
                    {on && service.is_center_service && (
                      <div className="flex items-center gap-2 mt-1.5 ml-6 flex-wrap">
                        <span className="text-[11px] text-[#8FA89B]">Price $</span>
                        <input type="number" min="0" step="1" value={ov[e.enhancer_id]?.price ?? ""} placeholder={String(service.price)}
                          onChange={(ev) => setOvField(e.enhancer_id, "price", ev.target.value)} data-testid={`svc-${service.id}-price-${e.enhancer_id}`}
                          className="w-24 rounded-lg border border-[#E5E0D8] px-2 py-1 text-sm bg-white" />
                        <span className="text-[11px] text-[#8FA89B]">Duration (min)</span>
                        <input type="number" min="5" step="5" value={ov[e.enhancer_id]?.duration_min ?? ""} placeholder={String(service.duration_min)}
                          onChange={(ev) => setOvField(e.enhancer_id, "duration_min", ev.target.value)} data-testid={`svc-${service.id}-dur-${e.enhancer_id}`}
                          className="w-24 rounded-lg border border-[#E5E0D8] px-2 py-1 text-sm bg-white" />
                        <span className="text-[10px] text-[#B5B0A6]">blank = service default</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {!service.is_center_service && (
            <p className="text-[11px] text-[#8FA89B] mb-3">This is a custom offering — practitioners are recorded here; the booking picker shows its owning enhancer.</p>
          )}
          <button onClick={save} disabled={saving || !dirty} data-testid={`svc-save-${service.id}`} className="eyw-btn !py-2 text-sm disabled:opacity-50 flex items-center gap-1.5">
            {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save
          </button>
        </div>
      )}
    </div>
  );
}

export function AvailabilityModal({ enhancer, onClose }) {
  const qc = useQueryClient();
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copyFrom, setCopyFrom] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ["mgr-avail", enhancer.enhancer_id],
    queryFn: () => api.get(`/manager/practitioners/${enhancer.enhancer_id}/availability`).then((r) => r.data),
  });
  useEffect(() => { if (data && rows === null) setRows(data.slots || []); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const list = rows || [];

  const add = () => setRows([...(rows || []), { day: 1, start: "09:00", end: "17:00" }]);
  const upd = (i, k, v) => setRows(list.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const rm = (i) => setRows(list.filter((_, idx) => idx !== i));

  const copyDayTo = (targetDays) => {
    const source = list.filter((r) => Number(r.day) === Number(copyFrom));
    if (source.length === 0) { toast.error(`Add a window on ${DAYS[copyFrom]} first, then copy it.`); return; }
    const kept = list.filter((r) => !targetDays.includes(Number(r.day)));
    const added = targetDays.flatMap((d) => source.map((s) => ({ day: d, start: s.start, end: s.end })));
    setRows([...kept, ...added]);
    toast.success(`Copied ${DAYS[copyFrom]}'s hours to ${targetDays.length} day(s)`);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/manager/practitioners/${enhancer.enhancer_id}/availability`, { slots: list });
      toast.success("Availability saved");
      qc.invalidateQueries({ queryKey: ["mgr-avail", enhancer.enhancer_id] });
      qc.invalidateQueries({ queryKey: ["mgr-service-practitioners"] });
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-7 max-h-[90vh] overflow-y-auto" data-testid="avail-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><CalendarClock size={22} className="text-[#144F75]" /> {enhancer.name || enhancer.email || "Practitioner"}'s availability</h3>
          <button onClick={onClose} data-testid="avail-close" className="text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        </div>
        <p className="text-sm text-[#4A5D6B] mb-4">Weekly appointment windows at your center. Bookings are still limited by room availability.</p>

        {isLoading && rows === null ? <Loader2 className="animate-spin text-[#144F75]" /> : (
          <div className="space-y-2 mb-4">
            {list.length === 0 && <p className="text-sm text-[#8FA89B]">No availability set — this practitioner is bookable any time a room is free. Add windows to restrict.</p>}
            {list.map((r, i) => (
              <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`avail-row-${i}`}>
                <select value={r.day} onChange={(e) => upd(i, "day", Number(e.target.value))} data-testid={`avail-day-${i}`} className="rounded-lg border border-[#E5E0D8] px-2.5 py-2 text-sm bg-white">
                  {DAYS.map((d, idx) => <option key={idx} value={idx}>{d}</option>)}
                </select>
                <input type="time" value={r.start} onChange={(e) => upd(i, "start", e.target.value)} data-testid={`avail-start-${i}`} className="rounded-lg border border-[#E5E0D8] px-2.5 py-2 text-sm bg-white" />
                <span className="text-[#8FA89B]">to</span>
                <input type="time" value={r.end} onChange={(e) => upd(i, "end", e.target.value)} data-testid={`avail-end-${i}`} className="rounded-lg border border-[#E5E0D8] px-2.5 py-2 text-sm bg-white" />
                <button onClick={() => rm(i)} data-testid={`avail-rm-${i}`} className="text-red-500 hover:text-red-600 p-1"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        )}

        <button onClick={add} data-testid="avail-add" className="eyw-btn-ghost !py-2 text-sm flex items-center gap-1.5 mb-3"><Plus size={15} /> Add window</button>

        {list.length > 0 && (
          <div className="rounded-xl bg-[#F9F7F3] border border-[#E5E0D8] p-3 mb-4" data-testid="avail-copy-tools">
            <p className="text-[11px] uppercase tracking-wide text-[#8FA89B] mb-2 flex items-center gap-1.5"><Copy size={12} /> Quick copy</p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-[#4A5D6B]">Copy</span>
              <select value={copyFrom} onChange={(e) => setCopyFrom(Number(e.target.value))} data-testid="avail-copy-from" className="rounded-lg border border-[#E5E0D8] px-2 py-1.5 text-sm bg-white">
                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
              <span className="text-xs text-[#4A5D6B]">to</span>
              <button onClick={() => copyDayTo([1, 2, 3, 4, 5])} data-testid="avail-copy-weekdays" className="rounded-full border border-[#144F75] text-[#144F75] px-3 py-1.5 text-xs hover:bg-[#144F75]/[0.06]">All weekdays (Mon–Fri)</button>
              <button onClick={() => copyDayTo([0, 1, 2, 3, 4, 5, 6])} data-testid="avail-copy-all" className="rounded-full border border-[#144F75] text-[#144F75] px-3 py-1.5 text-xs hover:bg-[#144F75]/[0.06]">Every day</button>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={save} disabled={saving} data-testid="avail-save" className="eyw-btn flex-1 justify-center disabled:opacity-60 flex items-center gap-1.5">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} Save availability
          </button>
        </div>
      </div>
    </div>
  );
}
